<template>
  <TransitionRoot as="template" :show="props.open">
    <Dialog as="div" class="relative z-30">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative rounded-md bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-[500px] max-w-[90vw] sm:p-0"
            >
              <button
                type="button"
                class="rounded-sm bg-white text-gray-500 hover:text-gray-400 focus:outline-gray-500 absolute top-2 right-4 p-2.5"
                @click="close"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="h-6 w-6" aria-hidden="true" />
              </button>

              <div
                class="flex flex-col gap-4 text-left sm:p-6 flex-grow-0 max-h-[80vh] overflow-hidden"
                v-if="!fileStep"
              >
                <div class="bg-white text-left rounded-t-lg">
                  <p class="text-gray-900 text-lg font-semibold">
                    {{ t("admin.settings.documents.upload.title") }}
                  </p>
                  <p class="text-gray-600 text-sm">
                    {{ t("admin.settings.documents.upload.description") }}
                  </p>
                </div>
                <!-- File drag and drop -->
                <div class="flex flex-col items-center">
                  <input
                    type="file"
                    ref="fileInput"
                    multiple
                    @input="onFileInput"
                    hidden
                    id="file-upload"
                  />
                  <label
                    for="file-upload"
                    ref="dropZone"
                    class="flex flex-col gap-2 items-center justify-center max-w-full w-[100vw] h-[126px] border border-gray-200 rounded-xl cursor-pointer hover:border-brand-700 group"
                    :class="{
                      'border-2 border-brand-700': isOverDropZone,
                      'h-[226px]': files.length === 0,
                    }"
                  >
                    <div class="p-1 rounded-full aspect-square bg-gray-50">
                      <UploadCloud
                        class="text-gray-600 w-8 h-8 p-2 rounded-full aspect-square bg-gray-100 group-hover:text-brand-700"
                      />
                    </div>
                    <p>
                      <span class="text-brand-700 font-semibold"
                        >Click to upload</span
                      >
                      <span v-if="!isMobile"> or drag and drop </span>
                    </p>
                  </label>
                </div>

                <!-- File list -->
                <div
                  v-if="files.length"
                  class="flex flex-col gap-3 max-h-[60vh]"
                >
                  <DocumentsUploadListItem
                    v-for="file in files"
                    :key="file.id"
                    :file="file"
                    @delete="removeFile"
                    withTrash
                  />
                </div>
              </div>

              <!-- File details -->
              <div
                class="flex flex-col text-left flex-grow-0 max-h-[80vh] overflow-hidden"
                v-else-if="
                  fileStep !== null &&
                  files.length > 0 &&
                  fileStep !== 'uploading'
                "
              >
                <div class="bg-white text-left rounded-t-lg sm:p-6 sm:pb-4">
                  <p class="text-gray-900 text-lg font-semibold">
                    {{ t("admin.settings.documents.upload_details.title") }}
                  </p>
                  <p class="text-gray-600 text-sm">
                    {{
                      t("admin.settings.documents.upload_details.description")
                    }}
                  </p>

                  <DocumentsUploadListItem class="mt-4" :file="fileStep" />
                </div>

                <div class="flex flex-col gap-4 p-4 bg-gray-50 overflow-auto">
                  <DynamicInputComponent
                    v-for="field in fields"
                    :key="field.key"
                    :modelValue="fileStep[field.key]"
                    @update:modelValue="
                      (field, value) => onInput(fileStep, field, value)
                    "
                    :field="field"
                    :form="fileStep"
                    :limitWidth="false"
                    :disabled="!!field.disabled"
                  />
                </div>
              </div>

              <!-- Uploading -->
              <div
                class="flex flex-col text-left flex-grow-0 max-h-[80vh] overflow-hidden"
                v-else-if="fileStep === 'uploading'"
              >
                <div class="bg-white text-left rounded-t-lg sm:p-6 sm:pb-5">
                  <p class="text-gray-900 text-lg font-semibold">
                    {{ t("admin.settings.documents.uploading.title") }}
                  </p>
                  <p class="text-gray-600 text-sm">
                    {{ t("admin.settings.documents.uploading.description") }}
                  </p>
                </div>

                <div
                  class="flex flex-col gap-3 max-h-[60vh] overflow-auto px-6"
                >
                  <DocumentsUploadListItem
                    v-for="file in files"
                    :key="file.id"
                    :file="file"
                  />
                </div>
              </div>

              <div
                v-if="hasFiles"
                class="mt-5 sm:mt-0 flex flex-col sm:flex-row-reverse sm:justify-between gap-2 px-6 py-4"
                :class="{
                  'border-t border-gray-200':
                    !isSelectingFiles && !isUploadingFiles,
                }"
              >
                <!-- User has selected files but can still add more. -->
                <primary-button
                  v-if="isSelectingFiles"
                  :label="'Add Details'"
                  class="w-full sm:w-auto"
                  @click="fileStep = files[0]"
                />

                <!-- User has completed all steps and is uploading. -->
                <primary-button
                  v-if="isUploadingFiles"
                  :label="'Done'"
                  class="w-full sm:w-auto"
                  @click="close(true)"
                  :disabled="!allFilesUploaded"
                />

                <!-- User started entering file details and can continue to the next file. -->
                <primary-button
                  v-if="!isSelectingFiles && !isUploadingFiles"
                  :label="lastStep ? 'Submit documents' : 'Next Document'"
                  class="w-full sm:w-auto"
                  @click="handleNextFile"
                  :disabled="disableNextStep"
                />

                <!-- User can go to a previous file. -->
                <secondary-button
                  v-if="hasPreviousFile"
                  label="Previous Document"
                  class="w-full sm:w-auto"
                  @click="handlePreviousFile"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import UploadCloud from "@/Tailwind/Icon/UploadCloud";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import { useAPIForm } from "@/Tailwind/Mixins/useAPIForm";
import route from "ziggy-js";
import { useBreakpoints } from "@/Tailwind/Mixins/useBreakpoints";
import DocumentsUploadListItem from "@/Tailwind/Components/Documents/DocumentsUploadListItem.vue";
import { useI18n } from "vue-i18n";
import { useDropZone } from "@vueuse/core";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";

const { t } = useI18n();

const emit = defineEmits(["close"]);
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  fields: {
    type: Array,
    default: () => [],
  },
});

const { isMobile } = useBreakpoints();

// File handling
const dropZone = ref();

const files = ref([]);

// File Stepper manamgement
const fileStep = ref(null);

const disableNextStep = computed(() => {
  return (
    // A non-empty title is required.
    !fileStep.value.title?.trim() ||
    // A tenant needs to be selected.
    !fileStep.value.tenant ||
    // At least one space or asset needs to be selected.
    !fileStep.value.spaces ||
    !fileStep.value.spaces?.length > 0 ||
    // The expiration reminder has been enabled but no dates were chosen.
    (fileStep.value.has_expiration_date &&
      (fileStep.value.expiration_date === null ||
        fileStep.value.reminder_date === null))
  );
});

const hasFiles = computed(() => {
  return files.value.length > 0;
});

const hasPreviousFile = computed(() => {
  const index = files.value.findIndex((file) => file.id === fileStep.value?.id);
  return index > 0;
});

const isUploadingFiles = computed(() => {
  return fileStep.value === "uploading";
});

const isSelectingFiles = computed(() => {
  return fileStep.value === null;
});

const handlePreviousFile = () => {
  const index = files.value.findIndex((file) => file.id === fileStep.value?.id);

  if (index === -1) {
    return;
  }

  fileStep.value = files.value[index - 1];
};

const handleNextFile = () => {
  const index = files.value.findIndex((file) => file.id === fileStep.value?.id);

  if (index === -1) {
    return;
  }

  if (index === files.value.length - 1) {
    fileStep.value = "uploading";
    uploadFiles();
  } else {
    fileStep.value = files.value[index + 1];
  }
};

const lastStep = computed(() => {
  return (
    fileStep.value === "uploading" ||
    fileStep.value?.id === files.value[files.value.length - 1]?.id
  );
});

// File Input management
const uploading = ref(false);

const onFileInput = (e) => {
  onDrop(e.target.files);

  // Allow selection of the same file again.
  e.target.value = null;
};

const onDrop = (inputFiles) => {
  if (!inputFiles || inputFiles.length === 0) {
    return;
  }

  // Assign each file a random id for internal state management.
  inputFiles = Array.from(inputFiles).map((file) =>
    useAPIForm({
      ...props.fields.reduce((acc, field) => {
        acc[field.key] = field.default;

        return acc;
      }, {}),
      id: Math.random().toString(36).substring(2, 15),
      title: file.name,
      size: file.size,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
      type: file.type,
      document: file,
      uploadProgress: 0,
      has_expiration_date: false,
      expiration_date: null,
      reminder_date: null,
    })
  );

  files.value = [...inputFiles, ...files.value];
};

const removeFile = (fileId) => {
  files.value = files.value.filter((file) => file.id !== fileId);
};

const { isOverDropZone } = useDropZone(dropZone, onDrop);

// File upload
const uploadFiles = () => {
  uploading.value = true;

  files.value
    .filter((file) => file.uploadProgress === 0)
    .forEach((file) => {
      file
        .transform((data) => ({
          ...data,
          space_ids: data.spaces,
        }))
        .post(route("settings.organisation.notes.store"), {
          onProgress: (progressObject) => {
            file.uploadProgress = progressObject.progress;
          },
          onFinish: () => {
            uploading.value = false;
          },
          onSuccess: () => {
            file.uploadProgress = 1.1;
          },
          onError: () => {
            file.uploadProgress = 0;
          },
        });
    });
};

const allFilesUploaded = computed(() => {
  return files.value.every((file) => file.uploadProgress === 1.1);
});

watch(
  () => props.open,
  () => {
    if (props.open) {
      files.value = [];
      fileStep.value = null;
    }
  }
);

const close = (refresh) => emit("close", refresh);

// We can't use vModel because the dynamic input component will
// emit different fields if it is a composite field
const onInput = (form, field, value) => {
  if (field.key === "expiration_date" && form["reminder_date"] !== null) {
    form["reminder_date"] = null;
  }

  form[field.key] = value;
};
</script>
